import { useFormik } from "formik";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import FormControl from "@mui/material/FormControl";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SendIcon from "@mui/icons-material/Send";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { updateCampaign } from "../../../redux/createNewCompany/actions";
import { LoadingButton } from "@mui/lab";
import "./Optimization.scss";
import { useMemo } from "react";
import { trackWizardEvent } from "../../../utils/productEvents";

const CTR_CPM = 0.2;
const CTR_CPC = 0.37;

const ceilClicksFromShows = (shows) => Math.ceil((CTR_CPM * shows) / 100);
const showsFromClicks = (clicks) => (clicks * 100) / CTR_CPC;
const SPECIAL_CAMPAIGNS_27 = [
  "9c621722-9f78-41ce-a45b-eb64ec8f747d",
  "f7fb6cd5-b62b-45a5-b84d-5d97da8a5079",
  "2a2bbbe5-e854-4c9e-a3c4-463c28f8e360",
];

const calculateDaysBetween = (dateStart, dateEnd) => {
  if (!dateStart || !dateEnd) return 0;
  const start = new Date(dateStart);
  const end = new Date(dateEnd);
  const timeDiff = end.getTime() - start.getTime();
  return Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;
};

const getDaysWord = (count) => {
  if ([11, 12, 13, 14].includes(count % 100)) return "дней";
  if (count % 10 === 1) return "день";
  if ([2, 3, 4].includes(count % 10)) return "дня";
  return "дней";
};

const formatForecastNumber = (value) =>
  Math.round(value).toLocaleString("ru-RU");

const formatBudget = (value) =>
  Number(value).toLocaleString("ru-RU", { maximumFractionDigits: 0 });

const INITIAL_VALUES = {
  optimization_target: "SHOWS",
  optimization_for: "FIX_PRICE",
  max_impressions: 0,
  fix_price: 120,
  min_consumption: 0,
  optimal_score: 0,
};

const createInitialValues = (data) => {
  if (!data) return INITIAL_VALUES;
  const result = { ...INITIAL_VALUES };
  for (const key in result) {
    if (key in data) {
      result[key] = data[key];
    }
  }
  return result;
};

const DataEqual = (objA, objB) => {
  if (!objA || !objB) return false;
  const properties = [
    "optimization_target",
    "optimization_for",
    "max_impressions",
    "fix_price",
    "min_consumption",
    "optimal_score",
  ];
  for (let key of properties) {
    if (objA[key] !== objB[key]) {
      return false;
    }
  }
  return true;
};

const ForecastColumn = ({ title, dailyValue, cycleValue, cyclePeriod, hasCycle }) => (
  <div className="payment-forecast-column">
    <h4 className="payment-forecast-column__title">{title}</h4>
    <div className="payment-forecast-column__body">
      <div className="payment-forecast-row">
        <span className="payment-forecast-row__period">В день</span>
        <span className="payment-forecast-metric__value">~ {dailyValue}</span>
      </div>
      {hasCycle ? (
        <div className="payment-forecast-row">
          <span className="payment-forecast-row__period">{cyclePeriod}</span>
          <span className="payment-forecast-metric__value">~ {cycleValue}</span>
        </div>
      ) : (
        <p className="payment-forecast-column__hint">
          Укажите даты медиаплана для прогноза на цикл
        </p>
      )}
    </div>
  </div>
);

const PaymentTypeToggle = ({ value, onChange, disabled }) => (
  <div className="payment-type-toggle" role="radiogroup" aria-label="Тип оплаты">
    <button
      type="button"
      role="radio"
      aria-checked={value === "SHOWS"}
      disabled={disabled}
      className={`payment-type-toggle__btn${value === "SHOWS" ? " payment-type-toggle__btn--active" : ""}`}
      onClick={() => onChange("SHOWS")}
    >
      <VisibilityOutlinedIcon sx={{ fontSize: 22, opacity: 0.9 }} />
      <span className="payment-type-toggle__text">
        <span className="payment-type-toggle__label">Показы</span>
        <span className="payment-type-toggle__sub">CPM</span>
      </span>
    </button>
    <button
      type="button"
      role="radio"
      aria-checked={value === "CLICKS"}
      disabled={disabled}
      className={`payment-type-toggle__btn${value === "CLICKS" ? " payment-type-toggle__btn--active" : ""}`}
      onClick={() => onChange("CLICKS")}
    >
      <TouchAppOutlinedIcon sx={{ fontSize: 22, opacity: 0.9 }} />
      <span className="payment-type-toggle__text">
        <span className="payment-type-toggle__label">Клики</span>
        <span className="payment-type-toggle__sub">CPC</span>
      </span>
    </button>
  </div>
);

const Optimization = (props) => {
  const dispatch = useDispatch();
  const handleChangeTab = () => props.handleChange("event", "6");
  const isSpecialCampaign = SPECIAL_CAMPAIGNS_27.includes(props.campaignID);
  const minPriceForClicks = isSpecialCampaign ? 27 : 42;
  const minPriceForShows = props.isTrial ? 78 : 120;

  const { handleChange, handleBlur, values, handleSubmit, isSubmitting, errors, setFieldValue } =
    useFormik({
      initialValues: createInitialValues(props.data?.optimization),
      enableReinitialize: true,
      validate: (values) => {
        let errors = {};
        if (
          values?.optimization_target === "CLICKS" &&
          values.fix_price < minPriceForClicks
        ) {
          errors.fix_price = `Значение должно быть больше либо равно ${minPriceForClicks}`;
        }
        if (
          values?.optimization_target === "SHOWS" &&
          props.isTrial &&
          values.fix_price < 78
        ) {
          errors.fix_price = "Значение должно быть больше либо равно 78";
        }
        if (
          values?.optimization_target === "SHOWS" &&
          !props.isTrial &&
          values.fix_price < 120
        ) {
          errors.fix_price = "Значение должно быть больше либо равно 120";
        }
        return errors;
      },
      onSubmit: async (values) => {
        if (!DataEqual(props.data.optimization, values)) {
          dispatch(
            updateCampaign(
              values,
              props.campaignID,
              "optimization",
              handleChangeTab
            )
          );
        } else {
          trackWizardEvent("wizard_tab_optimization", props.campaignID);
          handleChangeTab();
        }
      },
    });

  const dailyBudget = Number(props.data?.basic_config?.daily_budget) || 0;
  const cycleDays = calculateDaysBetween(
    props.data?.media_plan?.date_start,
    props.data?.media_plan?.date_end
  );
  const bid = Number(values.fix_price) || 0;
  const isShows = values.optimization_target === "SHOWS";

  const bidHelperText = errors.fix_price
    ? errors.fix_price
    : isShows
      ? props.isTrial
        ? "Минимум 78 ₽ за 1000 показов"
        : "Минимум 120 ₽ за 1000 показов"
      : isSpecialCampaign
        ? "Минимум 27 ₽ за клик"
        : "Минимум 42 ₽ за клик";

  const bidLabel = isShows ? "Ставка за 1000 показов" : "Ставка за клик";

  const forecast = useMemo(() => {
    if (dailyBudget <= 0 || bid <= 0 || errors.fix_price) return null;
    if (values.optimization_target === "SHOWS") {
      const dailyShows = (dailyBudget / bid) * 1000;
      const dailyClicks = ceilClicksFromShows(dailyShows);
      return {
        type: "SHOWS",
        dailyShows,
        dailyClicks,
        cycleShows: cycleDays > 0 ? dailyShows * cycleDays : null,
        cycleClicks: cycleDays > 0 ? dailyClicks * cycleDays : null,
      };
    }
    const dailyClicks = Math.ceil(dailyBudget / bid);
    const dailyShows = showsFromClicks(dailyClicks);
    const cycleClicks = cycleDays > 0 ? dailyClicks * cycleDays : null;
    const cycleShows =
      cycleClicks != null ? showsFromClicks(cycleClicks) : null;
    return {
      type: "CLICKS",
      dailyClicks,
      dailyShows,
      cycleClicks,
      cycleShows,
    };
  }, [dailyBudget, bid, cycleDays, values.optimization_target, errors.fix_price]);

  const handlePaymentTypeChange = (target) => {
    setFieldValue("optimization_target", target);
    const min = target === "SHOWS" ? minPriceForShows : minPriceForClicks;
    if (Number(values.fix_price) < min) {
      setFieldValue("fix_price", min);
    }
  };

  const handleBidBlur = (event) => {
    handleBlur(event);
    const num = Number(values.fix_price);
    if (Number.isNaN(num) || values.fix_price === "") return;
    const min = isShows ? minPriceForShows : minPriceForClicks;
    if (num < min) {
      setFieldValue("fix_price", min);
    }
  };

  const renderForecastContent = () => {
    if (dailyBudget <= 0) {
      return (
        <div className="payment-alert">
          <InfoOutlinedIcon sx={{ fontSize: 18, flexShrink: 0, mt: 0.1 }} />
          <span>Укажите дневной бюджет во вкладке «Основные настройки»</span>
        </div>
      );
    }
    if (!forecast) {
      return (
        <div className="payment-alert payment-alert--info">
          <InfoOutlinedIcon sx={{ fontSize: 18, flexShrink: 0, mt: 0.1 }} />
          <span>Укажите корректную ставку, чтобы увидеть прогноз</span>
        </div>
      );
    }
    const cyclePeriod =
      cycleDays > 0 ? `За цикл · ${cycleDays} ${getDaysWord(cycleDays)}` : "За цикл";

    const hasCycle = cycleDays > 0;

    if (forecast.type === "SHOWS") {
      return (
        <>
          <div className="payment-forecast-columns">
            <ForecastColumn
              title="Показы"
              dailyValue={formatForecastNumber(forecast.dailyShows)}
              cycleValue={formatForecastNumber(forecast.cycleShows)}
              cyclePeriod={cyclePeriod}
              hasCycle={hasCycle}
            />
            <ForecastColumn
              title="Клики"
              dailyValue={formatForecastNumber(forecast.dailyClicks)}
              cycleValue={formatForecastNumber(forecast.cycleClicks)}
              cyclePeriod={cyclePeriod}
              hasCycle={hasCycle}
            />
          </div>
          <p className="payment-forecast-note">
            Прогноз может отличаться от фактических значений.
          </p>
        </>
      );
    }
    return (
      <>
        <div className="payment-forecast-columns payment-forecast-columns--cpc">
          <ForecastColumn
            title="Клики"
            dailyValue={formatForecastNumber(forecast.dailyClicks)}
            cycleValue={formatForecastNumber(forecast.cycleClicks)}
            cyclePeriod={cyclePeriod}
            hasCycle={hasCycle}
          />
          <ForecastColumn
            title="Показы"
            dailyValue={formatForecastNumber(forecast.dailyShows)}
            cycleValue={formatForecastNumber(forecast.cycleShows)}
            cyclePeriod={cyclePeriod}
            hasCycle={hasCycle}
          />
        </div>
        <p className="payment-forecast-note">
          Прогноз может отличаться от фактических значений.
        </p>
      </>
    );
  };

  return (
    <div className="optimization__wrapper">
      <header className="payment-header title-text">
        <h2 style={{
          marginBottom: "10px",
          fontSize: "23px",
          fontWeight: "600",
          color: "#1a1a1a",
        }}>Оптимизация</h2>
      </header>
      <form className="payment-form" onSubmit={handleSubmit}>
        <div className="payment-sheet">
          <div className="payment-sheet__layout">
            <div className="payment-sheet__left payment-card">
              <div className="payment-block">
                <span className="payment-block__label">Тип оплаты</span>
                <span className="payment-block__hint">
                  За что списывается дневной бюджет
                </span>
                <PaymentTypeToggle
                  value={values.optimization_target}
                  onChange={handlePaymentTypeChange}
                  disabled={props.disable}
                />
              </div>
              <div className="payment-block payment-block--bid">
                <span className="payment-block__label">Ставка</span>
                <span className="payment-block__hint">{bidHelperText}</span>
                <FormControl className="payment-bid-field">
                  <TextField
                    disabled={props.disable}
                    error={Boolean(errors.fix_price)}
                    name="fix_price"
                    onChange={handleChange}
                    onBlur={handleBidBlur}
                    value={values.fix_price}
                    label={bidLabel}
                    type="number"
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">₽</InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
              </div>
            </div>

            <div className="payment-sheet__right payment-card">
              <div className="payment-row payment-row--forecast">
                <div className="payment-forecast-head">
                  <h3 className="payment-forecast-head__title">Прогноз</h3>
                  <div className="payment-context">
                    <span className="payment-context__chip">
                      Бюджет/день
                      <span className="payment-context__chip-value">
                        {dailyBudget > 0 ? `${formatBudget(dailyBudget)} ₽` : "—"}
                      </span>
                    </span>
                    <span className="payment-context__chip">
                      Цикл
                      <span className="payment-context__chip-value">
                        {cycleDays > 0
                          ? `${cycleDays} ${getDaysWord(cycleDays)}`
                          : "—"}
                      </span>
                    </span>
                  </div>
                </div>
                {renderForecastContent()}
              </div>
            </div>
          </div>
        </div>

        <div className="payment-actions">
          <Link to="/">
            <Button className="cancel" variant="outlined">
              Отмена
            </Button>
          </Link>
          <LoadingButton
            loading={isSubmitting}
            loadingPosition="end"
            endIcon={<SendIcon />}
            variant="contained"
            onClick={() => handleSubmit()}
          >
            {isSubmitting ? "Сохранение" : "Далее"}
          </LoadingButton>
        </div>
      </form>
    </div>
  );
};

export default Optimization;
